"use client"

import { FEATURE_CATALOG, FEATURE_KEYS, resolveFeatureFlags, type FeatureKey } from "@repo/types"
import { toast } from "sonner"
import { DendromapLoader } from "../../components/dendromap-loader"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Switch } from "../../components/ui/switch"
import { useFeatureDefaults, useUpdateFeatureDefaults } from "../../hooks/api/superadmin/use-superadmin-client"
import { useLabels } from "../../hooks/use-labels"
import { PageHeader } from "../../ui/layout/page-header"
import { apiErrorMessage } from "./client-detail.helpers"

/** Espace /superadmin : fonctionnalités activées par défaut pour tous les clients. */
export function FeaturesPage() {
  const { labels } = useLabels()
  const { data, isLoading } = useFeatureDefaults()
  const updateDefaults = useUpdateFeatureDefaults()

  if (isLoading || !data) return <DendromapLoader message="Chargement des fonctionnalités..." />
  const effective = resolveFeatureFlags(data.defaults, null)

  const toggle = async (key: FeatureKey, enabled: boolean) => {
    try {
      await updateDefaults.mutateAsync({ ...data.defaults, [key]: enabled })
      toast.success(`${FEATURE_CATALOG[key].label} : ${enabled ? "activée" : "désactivée"} par défaut`)
    } catch (error) {
      toast.error(apiErrorMessage(error, "Modification impossible"))
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.features}</PageHeader.Title>
        <PageHeader.Description>
          Valeur par défaut pour tous les clients. Une exception se règle dans la fiche du client et l&apos;emporte sur ce
          réglage (par exemple : désactivée par défaut, activée pour quelques clients en bêta).
        </PageHeader.Description>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fonctionnalités activables</CardTitle>
          <CardDescription>
            Une fonctionnalité désactivée disparaît du menu et sa page est bloquée. Pour l&apos;Assistant IA et les modèles de
            fiche, l&apos;API refuse aussi les appels (y compris depuis l&apos;app mobile).
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          {FEATURE_KEYS.map((key) => (
            <div key={key} className="flex items-center justify-between gap-4 py-4">
              <div className="min-w-0">
                <p className="font-medium text-foreground">{FEATURE_CATALOG[key].label}</p>
                <p className="text-sm text-muted-foreground">{FEATURE_CATALOG[key].description}</p>
              </div>
              <Switch
                aria-label={FEATURE_CATALOG[key].label}
                checked={effective[key]}
                disabled={updateDefaults.isPending}
                onCheckedChange={(checked) => toggle(key, checked)}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
